import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { courseUrl, moduleUrl, exerciseUrl } from '../../../../../utils/course-urls';
import classNames from 'classnames/bind';
import { useSearchQuery, SearchResultCourse, SearchResultModule, SearchResultExercise } from '../../../../../api/searchApi';
import { useGetAllCoursesQuery } from '../../../../../api/courseApi';
import { useGetUserCourseProgressQuery } from '../../../../../api/progressApi';
import { RootState } from '../../../../../store';
import styles from './SearchDropdown.module.scss';

const cx = classNames.bind(styles);

interface PageResult {
  path: string;
  label: string;
  icon: string;
}

interface SearchDropdownProps {
  query: string;
  onClose: () => void;
  userRole: string;
}

type TabKey = 'all' | 'pages' | 'courses' | 'modules' | 'exercises';

const EXERCISE_TYPE_ICONS: Record<string, string> = {
  blanks: 'fa-pen',
  gap: 'fa-puzzle-piece',
  multiple_choice: 'fa-list-ul',
  sequence: 'fa-sort-amount-down',
  matching: 'fa-exchange-alt',
  free_text: 'fa-align-left',
  pdf: 'fa-file-pdf',
};

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.5" y="7" width="9" height="6.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5.5 7V5.5C5.5 4.11929 6.61929 3 8 3C9.38071 3 10.5 4.11929 10.5 5.5V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const SearchDropdown: React.FC<SearchDropdownProps> = ({ query, onClose, userRole }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState<TabKey>('all');

  const isAdmin = userRole === 'ADMIN';
  const isCurator = userRole === 'CURATOR';
  const isAuthenticated = useSelector((state: RootState) => state.session.isAuthenticated);

  const { data: searchResults, isFetching } = useSearchQuery(
    { q: query, lang: i18n.language },
    { skip: query.length < 2 }
  );

  const { data: allCourses } = useGetAllCoursesQuery();
  const { data: courseProgressList } = useGetUserCourseProgressQuery(undefined, {
    skip: !isAuthenticated,
  });

  // Build a set of accessible course IDs (same logic as ExercisesBrowsePage)
  const accessibleCourseIds = useMemo(() => {
    const ids = new Set<number>();
    if (isAdmin) return ids; // admin sees everything as unlocked
    if (allCourses) {
      for (const course of allCourses) {
        if (course.is_free && course.id) ids.add(course.id);
      }
    }
    if (courseProgressList) {
      for (const cp of courseProgressList) {
        if (cp.course_id) ids.add(cp.course_id);
      }
    }
    return ids;
  }, [isAdmin, allCourses, courseProgressList]);

  const isCourseAccessible = (courseId: number | undefined): boolean => {
    if (isAdmin) return true;
    if (!courseId) return true;
    return accessibleCourseIds.has(courseId);
  };

  const isCourseItemFree = (course: SearchResultCourse): boolean => {
    if (course.is_free) return true;
    // Also check allCourses data for is_free
    const full = allCourses?.find((c) => c.id === course.id);
    return !!full?.is_free;
  };

  const pageResults = useMemo((): PageResult[] => {
    const pages: PageResult[] = [];
    if (isAdmin) {
      pages.push(
        { path: '/admin', label: t('sidebar.dashboard', 'Dashboard'), icon: 'fa-th-large' },
        { path: '/admin/courses', label: t('sidebar.courses', 'Courses'), icon: 'fa-book' },
        { path: '/admin/vocabulary', label: t('sidebar.vocabulary', 'Vocabulary'), icon: 'fa-language' },
        { path: '/admin/translations', label: t('sidebar.translations', 'Translations'), icon: 'fa-globe' },
        { path: '/admin/users', label: t('sidebar.students', 'Students'), icon: 'fa-user-graduate' },
        { path: '/admin/transactions', label: t('sidebar.transaction', 'Transactions'), icon: 'fa-money-bill-wave' },
        { path: '/admin/messages', label: t('sidebar.messages', 'Messages'), icon: 'fa-comment-dots' },
        { path: '/admin/ai-costs', label: t('sidebar.aiCosts', 'AI Costs'), icon: 'fa-dollar-sign' },
        { path: '/admin/logs', label: t('sidebar.logs', 'Logs'), icon: 'fa-file-alt' },
        { path: '/admin/profile', label: t('sidebar.profile', 'Profile'), icon: 'fa-user' },
        { path: '/admin/settings', label: t('sidebar.settings', 'Settings'), icon: 'fa-cog' },
        { path: '/admin/exercise-generator', label: t('topbar.exerciseGenerator', 'Exercise Generator'), icon: 'fa-magic' },
      );
    } else if (isCurator) {
      pages.push(
        { path: '/curator', label: t('sidebar.dashboard', 'Dashboard'), icon: 'fa-th-large' },
        { path: '/curator/users', label: t('sidebar.students', 'Students'), icon: 'fa-user-graduate' },
        { path: '/curator/messages', label: t('sidebar.messages', 'Messages'), icon: 'fa-comment-dots' },
        { path: '/curator/profile', label: t('sidebar.profile', 'Profile'), icon: 'fa-user' },
        { path: '/curator/settings', label: t('sidebar.settings', 'Settings'), icon: 'fa-cog' },
      );
    } else {
      pages.push(
        { path: '/dashboard', label: t('sidebar.dashboard', 'Dashboard'), icon: 'fa-home' },
        { path: '/courses', label: t('sidebar.courses', 'Courses'), icon: 'fa-book' },
        { path: '/exercises', label: t('topbar.exercises', 'Exercises'), icon: 'fa-dumbbell' },
        { path: '/vocabulary', label: t('sidebar.vocabulary', 'Vocabulary'), icon: 'fa-language' },
        { path: '/messages', label: t('sidebar.messages', 'Messages'), icon: 'fa-comment-dots' },
        { path: '/profile', label: t('sidebar.profile', 'Profile'), icon: 'fa-user' },
        { path: '/settings', label: t('sidebar.settings', 'Settings'), icon: 'fa-cog' },
      );
    }
    return pages;
  }, [isAdmin, isCurator, t]);

  const filteredPages = useMemo(() => {
    if (query.length < 2) return [];
    const lower = query.toLowerCase();
    return pageResults.filter((p) => p.label.toLowerCase().includes(lower));
  }, [query, pageResults]);

  // Sort: accessible items first within each category
  const sortByAccess = <T extends { course_id?: number }>(items: T[]): T[] => {
    if (isAdmin) return items;
    return [...items].sort((a, b) => {
      const aOk = isCourseAccessible(a.course_id) ? 0 : 1;
      const bOk = isCourseAccessible(b.course_id) ? 0 : 1;
      return aOk - bOk;
    });
  };

  const courses = searchResults?.courses || [];
  const modules = searchResults?.modules || [];
  const exercises = searchResults?.exercises || [];

  const sortedCourses = useMemo(() => {
    if (isAdmin) return courses;
    return [...courses].sort((a, b) => {
      const aOk = isCourseAccessible(a.id) || isCourseItemFree(a) ? 0 : 1;
      const bOk = isCourseAccessible(b.id) || isCourseItemFree(b) ? 0 : 1;
      return aOk - bOk;
    });
  }, [courses, isAdmin, accessibleCourseIds, allCourses]);

  const sortedModules = useMemo(() => sortByAccess(modules), [modules, isAdmin, accessibleCourseIds]);
  const sortedExercises = useMemo(() => sortByAccess(exercises), [exercises, isAdmin, accessibleCourseIds]);

  const allItems = useMemo(() => {
    const items: { path: string }[] = [];
    const show = (tab: TabKey) => activeTab === 'all' || activeTab === tab;
    if (show('courses')) sortedCourses.forEach((c) => items.push({ path: c.slug ? courseUrl(c.slug) : '#' }));
    if (show('modules')) sortedModules.forEach((m) => {
      const accessible = isCourseAccessible(m.course_id);
      if (!m.course_slug) { items.push({ path: '#' }); return; }
      items.push({ path: accessible ? moduleUrl(m.course_slug, m.slug) : courseUrl(m.course_slug) });
    });
    if (show('exercises')) sortedExercises.forEach((e) => {
      const accessible = isCourseAccessible(e.course_id);
      if (accessible) {
        items.push({ path: e.course_slug && e.module_slug && e.slug ? exerciseUrl(e.course_slug, e.module_slug, e.slug) : '#' });
      } else {
        items.push({ path: e.course_slug ? courseUrl(e.course_slug) : '#' });
      }
    });
    if (show('pages')) filteredPages.forEach((p) => items.push({ path: p.path }));
    return items;
  }, [activeTab, filteredPages, sortedCourses, sortedModules, sortedExercises, accessibleCourseIds, isAdmin]);

  useEffect(() => { setHighlightedIndex(-1); }, [activeTab, allItems.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightedIndex((p) => (p < allItems.length - 1 ? p + 1 : 0)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightedIndex((p) => (p > 0 ? p - 1 : allItems.length - 1)); }
      if (e.key === 'Enter' && highlightedIndex >= 0) { e.preventDefault(); const item = allItems[highlightedIndex]; if (item && item.path !== '#') { navigate(item.path); onClose(); } }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [allItems, highlightedIndex, navigate, onClose]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (query.length < 2) return null;

  const totalResults = filteredPages.length + courses.length + modules.length + exercises.length;
  const tabCounts: Record<TabKey, number> = { all: totalResults, pages: filteredPages.length, courses: courses.length, modules: modules.length, exercises: exercises.length };
  const tabs: { key: TabKey; label: string }[] = [
    { key: 'all', label: t('search.all', 'All') },
    { key: 'courses', label: t('search.courses', 'Courses') },
    { key: 'modules', label: t('search.modules', 'Modules') },
    { key: 'exercises', label: t('search.exercises', 'Exercises') },
    { key: 'pages', label: t('search.pages', 'Pages') },
  ];
  const show = (tab: TabKey) => activeTab === 'all' || activeTab === tab;
  let globalIdx = 0;
  const getIdx = () => globalIdx++;
  const go = (path: string) => { if (path !== '#') { navigate(path); onClose(); } };

  const renderAccessBadge = (accessible: boolean, isFree?: boolean) => {
    if (isAdmin) return null;
    if (accessible || isFree) {
      return <span className={cx('accessBadge', 'open')}><i className="fas fa-arrow-right" /></span>;
    }
    return (
      <span className={cx('accessBadge', 'locked')}>
        <LockIcon />
      </span>
    );
  };

  return (
    <div className={cx('dropdown')} ref={dropdownRef}>
      <div className={cx('tabs')}>
        {tabs.map((tab) => (
          <button key={tab.key} className={cx('tab', { active: activeTab === tab.key })} onClick={() => setActiveTab(tab.key)}>
            {tab.label}
            {tabCounts[tab.key] > 0 && <span className={cx('tabCount')}>{tabCounts[tab.key]}</span>}
          </button>
        ))}
      </div>
      <div className={cx('list')}>
        {isFetching && totalResults === 0 ? (
          <div className={cx('empty')}><i className="fas fa-spinner fa-spin" /></div>
        ) : totalResults === 0 ? (
          <div className={cx('empty')}>{t('topbar.noResults', 'No results found')}</div>
        ) : (
          <>
            {show('courses') && sortedCourses.length > 0 && (
              <>
                {activeTab === 'all' && <div className={cx('categoryLabel')}>{t('search.courses', 'Courses')}</div>}
                {sortedCourses.map((c) => {
                  const idx = getIdx();
                  const accessible = isCourseAccessible(c.id);
                  const isFree = isCourseItemFree(c);
                  return (
                    <button key={`c-${c.id}`} className={cx('item', { highlighted: idx === highlightedIndex, lockedItem: !accessible && !isFree && !isAdmin })} onClick={() => go(c.slug ? courseUrl(c.slug) : '#')} onMouseEnter={() => setHighlightedIndex(idx)}>
                      <div className={cx('icon', { lockedIcon: !accessible && !isFree && !isAdmin })}>
                        {!accessible && !isFree && !isAdmin ? <LockIcon /> : <i className="fas fa-book" />}
                      </div>
                      <div className={cx('itemContent')}>
                        <span className={cx('itemName')}>{c.name}</span>
                        {c.level && <span className={cx('itemMeta')}>{c.level}{c.module_count ? ` · ${c.module_count} ${t('search.modulesCount', 'modules')}` : ''}</span>}
                      </div>
                      {renderAccessBadge(accessible, isFree)}
                    </button>
                  );
                })}
              </>
            )}
            {show('modules') && sortedModules.length > 0 && (
              <>
                {activeTab === 'all' && <div className={cx('categoryLabel')}>{t('search.modules', 'Modules')}</div>}
                {sortedModules.map((m) => {
                  const idx = getIdx();
                  const accessible = isCourseAccessible(m.course_id);
                  const path = !m.course_slug ? '#' : accessible ? moduleUrl(m.course_slug, m.slug) : courseUrl(m.course_slug);
                  return (
                    <button key={`m-${m.id}`} className={cx('item', { highlighted: idx === highlightedIndex, lockedItem: !accessible && !isAdmin })} onClick={() => go(path)} onMouseEnter={() => setHighlightedIndex(idx)}>
                      <div className={cx('icon', { lockedIcon: !accessible && !isAdmin })}>
                        {!accessible && !isAdmin ? <LockIcon /> : <i className="fas fa-layer-group" />}
                      </div>
                      <div className={cx('itemContent')}>
                        <span className={cx('itemName')}>{m.name}</span>
                        <span className={cx('itemMeta')}>{m.course_name}{m.exercise_count ? ` · ${m.exercise_count} ${t('search.exercisesCount', 'exercises')}` : ''}</span>
                      </div>
                      {renderAccessBadge(accessible)}
                    </button>
                  );
                })}
              </>
            )}
            {show('exercises') && sortedExercises.length > 0 && (
              <>
                {activeTab === 'all' && <div className={cx('categoryLabel')}>{t('search.exercises', 'Exercises')}</div>}
                {sortedExercises.map((e) => {
                  const idx = getIdx();
                  const accessible = isCourseAccessible(e.course_id);
                  const path = accessible
                    ? (e.course_slug && e.module_slug && e.slug ? exerciseUrl(e.course_slug, e.module_slug, e.slug) : '#')
                    : (e.course_slug ? courseUrl(e.course_slug) : '#');
                  return (
                    <button key={`e-${e.id}`} className={cx('item', { highlighted: idx === highlightedIndex, lockedItem: !accessible && !isAdmin })} onClick={() => go(path)} onMouseEnter={() => setHighlightedIndex(idx)}>
                      <div className={cx('icon', { lockedIcon: !accessible && !isAdmin })}>
                        {!accessible && !isAdmin ? <LockIcon /> : <i className={`fas ${EXERCISE_TYPE_ICONS[e.type || ''] || 'fa-pen'}`} />}
                      </div>
                      <div className={cx('itemContent')}>
                        <span className={cx('itemName')}>{e.name}</span>
                        <span className={cx('itemMeta')}>{e.module_name}{e.course_name ? ` · ${e.course_name}` : ''}</span>
                      </div>
                      {renderAccessBadge(accessible)}
                    </button>
                  );
                })}
              </>
            )}
            {show('pages') && filteredPages.length > 0 && (
              <>
                {activeTab === 'all' && <div className={cx('categoryLabel')}>{t('search.pages', 'Pages')}</div>}
                {filteredPages.map((p) => { const idx = getIdx(); return (
                  <button key={p.path} className={cx('item', { highlighted: idx === highlightedIndex })} onClick={() => go(p.path)} onMouseEnter={() => setHighlightedIndex(idx)}>
                    <div className={cx('icon')}><i className={`fas ${p.icon}`} /></div>
                    <div className={cx('itemContent')}><span className={cx('itemName')}>{p.label}</span></div>
                  </button>
                ); })}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchDropdown;
